import React, { KeyboardEvent, useCallback, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { useFlags } from 'launchdarkly-react-client-sdk';

import useTracker from 'src/lib/js/hooks/useTracker';
import { Channel } from 'src/public/js/siteUtilities';
import { ShowForUS } from 'src/shared/components/common/show_for_us/ShowForUS';

import { Modal, ModalOverlay, useModal } from 'shared/components/common/modal';
import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext';

import { PwlessAuth } from 'public/components/default_template/online_ordering/account/pwlessAuth/PwlessAuth';
import AnimatedSection from 'public/components/default_template/online_ordering/checkout/AnimatedSection';
import CheckoutSection from 'public/components/default_template/online_ordering/checkout/CheckoutSection';
import CustomerInfoSection from 'public/components/default_template/online_ordering/checkout/CustomerInfoSection';
import { ShippingSection } from 'public/components/default_template/online_ordering/checkout/ShippingSection';
import { AuthenticationSource } from 'public/components/default_template/online_ordering/checkout/checkoutUtils';
import { PackagingOptions } from 'public/components/default_template/online_ordering/checkout/packaging/PackagingOptions';
import CreditCardFormSPI from 'public/components/default_template/online_ordering/checkout/payment/CreditCardFormSPI';
import OrderPrices from 'public/components/default_template/online_ordering/checkout/payment/OrderPrices';
import { AcceptedPaymentMethods } from 'public/components/default_template/online_ordering/checkout/payment/SavedCreditCards';
import TipSelector from 'public/components/default_template/online_ordering/checkout/payment/TipSelector';
import { useCart } from 'public/components/online_ordering/CartContext';
import { useCheckout } from 'public/components/online_ordering/CheckoutContext';
import { useCustomer } from 'public/components/online_ordering/CustomerContextCommon';
import { PaymentOption, usePayment } from 'public/components/online_ordering/PaymentContext';

import DiscountsSection from './Discounts/DiscountsSection';
import Fundraising from './Fundraising';
import PaymentOptionsSection from './PaymentOptionsSection';

interface PaymentSectionProps {
  isExpanded: boolean;
}

const PaymentSection = ({ isExpanded }: PaymentSectionProps) => {
  const { ooRestaurant } = useRestaurant();
  const { cart, channel } = useCart();
  const { customer } = useCustomer();
  const { paymentOption, fundraisingEnabled, tipEnabled, setTipAmount, setTipPercent } = usePayment();
  const { setPackagingOptions, packagingOptions } = useCheckout();
  const [selectedTipButton, setSelectedTipButton] = useState('');
  const { isOpen, onClose, onOpen } = useModal();
  const tracker = useTracker();
  const { enabledPaymentOptions } = useCheckout();
  const { setValue } = useFormContext();

  const clickLogIn = useCallback(() => {
    onOpen();
    tracker.track('Started Authentication', { source: AuthenticationSource.GuestCheckout } );
  }, [onOpen, tracker]);

  const keyDownLogIn = useCallback((e: KeyboardEvent) => {
    switch(e.key) {
      case ' ':
      case 'Enter':
        e.preventDefault();
        e.stopPropagation();
        clickLogIn();
        break;
    }
  }, [clickLogIn]);

  const onTipChange = useCallback((buttonId: string, tip: number, percent?: number) => {
    setSelectedTipButton(buttonId);
    setTipAmount(tip);
    setTipPercent(percent);
    // Setting a precomputed tip amount should clear the custom tip
    if(buttonId !== 'custom-tip') {
      setValue('custom-tip-amount', undefined);
    }
  }, [setTipPercent, setTipAmount, setSelectedTipButton, setValue]);

  if(!ooRestaurant || !cart) {
    return null;
  }

  const canPayAtCheckout = !enabledPaymentOptions.has(PaymentOption.UponReceipt) || enabledPaymentOptions.size > 1;

  return (
    <>
      {customer ?
        <CustomerInfoSection /> :
        <ShowForUS>
          <CheckoutSection title="Checkout as a guest">
            <div id="alreadyhaveacheckoutcta">
              <span>Already have a Toast account? For faster checkout,{' '}</span>
              <span aria-labelledby="alreadyhaveacheckoutcta" tabIndex={0} role="button" className="logInLink" onKeyDown={keyDownLogIn} onClick={clickLogIn}>log in.</span>
            </div>
            <Modal isOpen={isOpen} onClose={onClose}>
              <ModalOverlay fadeIn fadeOut />
              <PwlessAuth source={AuthenticationSource.GuestCheckout} />
            </Modal>
          </CheckoutSection>
        </ShowForUS>}

      <CheckoutSection title="Payment">
        {canPayAtCheckout && <AcceptedPaymentMethods />}
        <PaymentOptionsSection />
        <ShowForUS>
          <CreditCardInfo paymentOption={paymentOption} />
        </ShowForUS>
      </CheckoutSection>

      {!customer && <CustomerInfoSection />}

      {channel === Channel.ECOMM && <ShippingSection />}

      <CheckoutSection title={'Discounts'}>
        {
          isExpanded && <DiscountsSection />
        }
      </CheckoutSection>
      <AnimatedSection expanded={tipEnabled && channel !== Channel.ECOMM}>
        <CheckoutSection>
          <TipSelector tipOptions={cart.preComputedTips} selectedTipButton={selectedTipButton} onChange={onTipChange} />
        </CheckoutSection>
      </AnimatedSection>
      {
        ooRestaurant?.packagingConfig?.enabled &&
          <CheckoutSection title="Packaging options">
            <PackagingOptions packagingConfig={ooRestaurant.packagingConfig} selectedPackagingOptions={packagingOptions} onPackagingOptionsUpdate={setPackagingOptions} />
          </CheckoutSection>
      }
      {fundraisingEnabled &&
        <CheckoutSection>
          <FundraisingSection />
        </CheckoutSection>}
      <OrderPrices canShowSurcharges={isExpanded} />
    </>
  );
};

const CreditCardInfo = ({ paymentOption }: { paymentOption: PaymentOption | null }) => {
  const { ooToastCashSpend: ooToastCashSpendEnabled } = useFlags();
  const { orderTotal } = useCheckout();

  return (
    <AnimatedSection expanded={paymentOption === PaymentOption.PayNow}>
      <CreditCardFormSPI allowToastCash={ooToastCashSpendEnabled} disableSPI={orderTotal <= 0} />
    </AnimatedSection>
  );
};

const FundraisingSection = () => {
  const { ooRestaurant } = useRestaurant();
  const { cart } = useCart();
  const { tipAmount, fundraisingEnabled, setFundraisingAmount, fundraisingAmount } = usePayment();
  const fundraisingCampaign = ooRestaurant?.fundraisingConfig;
  return (
    <Fundraising
      fundraisingCampaign={fundraisingCampaign}
      onChange={(amount: number) => setFundraisingAmount(amount)}
      orderTotal={(cart?.order?.totalV2 || 0) + tipAmount}
      fundraisingEnabled={fundraisingEnabled}
      fundraisingAmount={fundraisingAmount} />
  );
};

export default PaymentSection;
